import React from 'react';
import { Link } from 'react-router-dom';
import '../dashboard.css';
import DashCard from '../overview/DashCard';
import { useAuth } from '../../../contexts/AuthContext';

export interface SectionEmptyState {
  /** e.g. "لا توجد محاضر حتى الآن". */
  title: string;
  text: string;
  /** Create action — shown to staff only; clients never create module records here. */
  action?: { to: string; label: string };
}

interface SectionCardProps {
  /** bootstrap-icons name, shown as a tinted chip before the title. */
  icon: string;
  title: string;
  caption?: string;
  /** Route of the module's list page ("View all" link). */
  to?: string;
  loading?: boolean;
  /** Full-width stat strip under the header (a row of <KpiStat>). */
  stats?: React.ReactNode;
  /**
   * When set, the module has no records yet: the card shows this compact
   * empty state instead of stats/children, so the module stays visible
   * rather than silently disappearing from the dashboard.
   */
  empty?: SectionEmptyState | null;
  children?: React.ReactNode;
}

/** Shared shell for a per-module dashboard section (all three role dashboards). */
const SectionCard: React.FC<SectionCardProps> = ({
  icon,
  title,
  caption,
  to,
  loading = false,
  stats,
  empty,
  children,
}) => {
  const { user } = useAuth();
  const canCreate = user?.role === 'admin' || user?.role === 'editor';

  return (
    <DashCard className="dash-section dash-card-full" icon={icon} title={title} caption={caption} to={empty ? undefined : to}>
      {loading ? (
        <div className="dash-section-body" aria-hidden="true">
          <span className="dash-skel" style={{ height: 28, width: '50%' }} />
          <span className="dash-skel" style={{ height: 10, borderRadius: 99 }} />
          <span className="dash-skel" style={{ height: 36 }} />
        </div>
      ) : empty ? (
        <div className="dash-empty-inline">
          <span className="dash-empty-tile" aria-hidden="true"><i className="bi bi-inbox" /></span>
          <div className="dash-empty-inline-text">
            <span className="dash-empty-title">{empty.title}</span>
            <span className="dash-empty-text">{empty.text}</span>
          </div>
          {empty.action && canCreate && (
            <Link to={empty.action.to} className="dash-btn dash-btn-soft">
              <i className="bi bi-plus-lg" aria-hidden="true" />
              {empty.action.label}
            </Link>
          )}
        </div>
      ) : (
        <>
          {stats && <div className="dash-section-kpis">{stats}</div>}
          {children && <div className="dash-section-body">{children}</div>}
        </>
      )}
    </DashCard>
  );
};

export default SectionCard;
